
import React from "react";

import { SectionHeader } from "@/components/ui/section-header";
import { Helmet } from "react-helmet";

export default function TermsPage() {
  return (
    <>
      <Helmet>
        <title>Terms of Use | Grandpal</title>
        <meta name="description" content="Terms and conditions for using Grandpal's nursing home search and Fair Deal support services." />
      </Helmet>
      
      <div className="container max-w-4xl py-16">
        <SectionHeader 
          title="Terms of Use"
          subtitle="Last updated: April 20, 2025"
          centered
        />
        
        <div className="prose prose-lg max-w-none">
          <h3>1. Acceptance of Terms</h3>
          <p>
            By accessing and using Grandpal's website, mobile applications, and services (collectively, the "Services"), you agree to be bound by these Terms of Use, our Privacy Policy, and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing our Services.
          </p>
          
          <h3>2. Description of Services</h3>
          <p>
            Grandpal provides an online platform that connects families seeking nursing home care with nursing homes across Ireland. Our Services include, but are not limited to:
          </p>
          <ul>
            <li>Searching and browsing nursing home listings</li>
            <li>Accessing information about the Fair Deal Scheme</li>
            <li>Submitting inquiries to nursing homes</li>
            <li>Creating user accounts to save preferences</li>
            <li>Accessing educational resources about elder care</li>
          </ul>
          
          <h3>3. User Accounts</h3>
          <p>
            When you create an account with us, you guarantee that the information you provide is accurate, complete, and current at all times. Inaccurate, incomplete, or obsolete information may result in the immediate termination of your account.
          </p>
          <p>
            You are responsible for maintaining the confidentiality of your account and password, including but not limited to restricting access to your computer and/or account. You accept responsibility for all activities that occur under your account and/or password.
          </p>
          
          <h3>4. User Content and Conduct</h3>
          <p>
            Our Services may allow you to post, link, store, share and otherwise make available certain information, text, graphics, or other material. By providing User Content, you grant us the right to use, modify, publicly perform, publicly display, reproduce, and distribute such content on and through our Services.
          </p>
          <p>
            You agree not to use our Services to:
          </p>
          <ul>
            <li>Post or transmit any content that is unlawful, harmful, threatening, abusive, harassing, defamatory, or otherwise objectionable</li>
            <li>Impersonate any person or entity or falsely state or misrepresent your affiliation with a person or entity</li>
            <li>Engage in any activity that interferes with or disrupts the Services or servers and networks connected to the Services</li>
            <li>Attempt to gain unauthorized access to any portion of the Services or any other accounts, computer systems, or networks connected to the Services</li>
            <li>Harvest or collect information about users without their consent</li>
          </ul>
          
          <h3>5. Nursing Home Listings</h3>
          <p>
            Grandpal strives to provide accurate and up-to-date information about nursing homes. However, we do not own, operate, or control any of the nursing homes listed on our platform. The information provided is based on publicly available data and information provided by the nursing homes themselves.
          </p>
          <p>
            We cannot guarantee the accuracy, completeness, or reliability of any nursing home information. Users should always verify information directly with the nursing home before making any decisions.
          </p>
          
          <h3>6. Fair Deal Information</h3>
          <p>
            Information provided about the Fair Deal Scheme (Nursing Homes Support Scheme) is for general guidance only and should not be considered as legal, financial, or professional advice. For specific advice tailored to your situation, please consult with a qualified professional.
          </p>
          <p>
            Grandpal does not process Fair Deal applications and is not affiliated with the Health Service Executive (HSE) or any governmental department responsible for administering the scheme.
          </p>
          
          <h3>7. Intellectual Property</h3>
          <p>
            The Service and its original content (excluding User Content), features, and functionality are and will remain the exclusive property of Grandpal and its licensors. The Service is protected by copyright, trademark, and other laws of Ireland and foreign countries. Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of Grandpal.
          </p>
          
          <h3>8. Links to Other Websites</h3>
          <p>
            Our Service may contain links to third-party websites or services that are not owned or controlled by Grandpal. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites or services. We do not warrant the offerings of any of these entities/individuals or their websites.
          </p>
          
          <h3>9. Limitation of Liability</h3>
          <p>
            To the maximum extent permitted by law, Grandpal shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from:
          </p>
          <ul>
            <li>Your access to or use of or inability to access or use the Service</li>
            <li>Any conduct or content of any third party on the Service</li>
            <li>Any content obtained from the Service</li>
            <li>Unauthorized access, use, or alteration of your transmissions or content</li>
          </ul>
          
          <h3>10. Indemnification</h3>
          <p>
            You agree to defend, indemnify, and hold harmless Grandpal and its licensee and licensors, and their employees, contractors, agents, officers, and directors, from and against any and all claims, damages, obligations, losses, liabilities, costs or debt, and expenses (including but not limited to attorney's fees) arising from your use of and access to the Service.
          </p>
          
          <h3>11. Changes to Terms</h3>
          <p>
            We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
          </p>
          <p>
            By continuing to access or use our Service after any revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, you are no longer authorized to use the Service.
          </p>
          
          <h3>12. Governing Law</h3>
          <p>
            These Terms shall be governed and construed in accordance with the laws of Ireland, without regard to its conflict of law provisions. Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.
          </p>
          
          <h3>13. Contact Us</h3>
          <p>
            If you have any questions about these Terms, please contact us at <a href="mailto:support@grandpal.co" className="text-grandpal-green hover:underline">support@grandpal.co</a>.
          </p>
        </div>
      </div>
    </>
  );
}
